import { useEffect, useRef, useState } from "react";
import type { LeakSiteConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { useSpeaking } from "../lib/useSpeaking";
import { tx, txr } from "../lib/tx";

/**
 * Attacker side, in the hacker room. Two skins of the same publish cinematic:
 *   - "extortion" (default): a dark-web leak blog. A "new victim" notification
 *     lands, the operator mutters, a publish pipeline runs and an extortion post
 *     goes live (double extortion: pay or the stolen data is leaked).
 *   - "market": a darknet MARKETPLACE. The operator packages the stolen data and
 *     posts a LISTING for sale (price in crypto, buyers online), ending in a SALE.
 * Ends on a Continue button.
 */
export function LeakSite({
  config,
  onContinue,
}: {
  config: LeakSiteConfig;
  onContinue: () => void;
}) {
  const market = config.mode === "market";
  const steps =
    config.steps ??
    (market
      ? [
          "Packaging stolen dataset",
          "Encrypting for escrow",
          "Uploading to market mirror",
          "Publishing listing",
        ]
      : [
          "Packaging exfiltrated archive",
          "Uploading to mirror nodes",
          "Generating victim entry",
          "Publishing extortion post",
        ]);
  const [toast, setToast] = useState(false);
  const [stepIdx, setStepIdx] = useState(-1);
  const [progress, setProgress] = useState(0);
  const [live, setLive] = useState(false);
  const [sold, setSold] = useState(false);
  const mounted = useRef(true);
  const speaking = useSpeaking();

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    if (config.line) void audio.prefetchSpeech(config.line, { voice: config.voiceId || "ash", lang: config.language || "en-US" });
    mounted.current = true;
    const timers: number[] = [];
    timers.push(window.setTimeout(() => mounted.current && setToast(true), 350));
    if (config.line) {
      timers.push(
        window.setTimeout(
          () => audio.speak(config.line!, { voice: config.voiceId || "ash", lang: config.language || "en-US" }),
          250
        )
      );
    }
    steps.forEach((_, i) => {
      timers.push(window.setTimeout(() => mounted.current && setStepIdx(i), 1500 + i * 1200));
    });
    const total = 1500 + steps.length * 1200 + 500;
    const t0 = performance.now();
    const tick = window.setInterval(() => {
      const k = Math.min(1, (performance.now() - t0) / total);
      if (mounted.current) setProgress(Math.round(k * 100));
      if (k >= 1) {
        window.clearInterval(tick);
        if (mounted.current) {
          setStepIdx(steps.length);
          setLive(true);
          // market mode: a buyer purchases shortly after the listing goes live.
          if (market) timers.push(window.setTimeout(() => mounted.current && setSold(true), 2200));
        }
      }
    }, 80);
    timers.push(tick);
    return () => {
      mounted.current = false;
      audio.cancelSpeech();
      timers.forEach((t) => {
        window.clearTimeout(t);
        window.clearInterval(t);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const post = config.post;
  const mk = config.market;

  return (
    <div className="leaksite">
      <div className={`leak-app ${market ? "market" : ""}`}>
        <div className="leak-titlebar">
          <span className="leak-onion">{market ? "\u{1F6D2}" : "\u{1F9C5}"}</span>
          <span className="leak-url">{config.siteName ?? (market ? tx("BlackHarbor Market") : tx("ShadowLeaks"))}</span>
          <span className="leak-tor">{tx(".onion")}</span>
          <span className="leak-badge">{market ? tx("vendor console") : tx("operator console")}</span>
        </div>

        {toast && (
          <div className="leak-toast">
            <span className="leak-toast-dot" />
            {config.newVictimToast ?? (market ? tx("New dataset ready: {victim}", { victim: config.victim }) : tx("New victim online: {victim}", { victim: config.victim }))}
          </div>
        )}

        <div className="leak-body">
          <div className="leak-left">
            <div className="leak-card-h">{market ? tx("Listing pipeline") : tx("Publish queue")}</div>
            <ul className="dfgen-steps">
              {steps.map((s, i) => {
                const state = i < stepIdx || live ? "done" : i === stepIdx ? "active" : "wait";
                return (
                  <li key={i} className={`dfgen-step ${state}`}>
                    <span className="dfgen-step-ico">{state === "done" ? "\u2713" : ""}</span>
                    {s}
                  </li>
                );
              })}
            </ul>
            <div className="dfgen-progress">
              <div className="dfgen-progress-bar" style={{ width: `${progress}%` }} />
            </div>
            <div className="dfgen-progress-label">
              {live ? (market ? tx("Listing live") : tx("Post published")) : `${market ? "Listing" : "Publishing"}\u2026 ${progress}%`}
            </div>
          </div>

          <div className="leak-right">
            <div className="leak-card-h">{market ? tx("Marketplace listing") : tx("Live post")}</div>

            {market ? (
              <div className={`leak-listing ${live ? "live" : "pending"}`}>
                <div className="leak-listing-top">
                  <span className="leak-skull">{"\u{1F4E6}"}</span>
                  <h2 className="leak-post-headline">{post.headline}</h2>
                </div>
                <p className="leak-post-body">{post.body}</p>
                {mk && (
                  <>
                    <div className="leak-price">
                      <span className="leak-price-big">{mk.price}</span>
                      {mk.priceSub && <span className="leak-price-sub">{mk.priceSub}</span>}
                    </div>
                    {mk.packageItems && mk.packageItems.length > 0 && (
                      <div className="leak-sample">
                        <div className="leak-sample-h">{tx("In this package")}</div>
                        {mk.packageItems.map((s, i) => (
                          <div key={i} className="leak-sample-row">{"\u{1F4C4}"} {s}</div>
                        ))}
                      </div>
                    )}
                    {typeof mk.buyersOnline === "number" && (
                      <div className="leak-buyers">
                        <span className="leak-buyers-dot" /> {txr("{buyersOnline} buyers viewing now", { buyersOnline: mk.buyersOnline })}
                      </div>
                    )}
                  </>
                )}
                {sold && mk?.saleToast && <div className="leak-sold">{"\u2713"} {mk.saleToast}</div>}
              </div>
            ) : (
              <div className={`leak-post ${live ? "live" : "pending"}`}>
                <div className="leak-post-victim">
                  <span className="leak-skull">{"\u2620"}</span> {config.victim}
                </div>
                <h2 className="leak-post-headline">{post.headline}</h2>
                <p className="leak-post-body">{post.body}</p>
                {post.deadline && <div className="leak-post-deadline">{"\u23F1"} {post.deadline}</div>}
                {post.sample && post.sample.length > 0 && (
                  <div className="leak-sample">
                    <div className="leak-sample-h">{tx("Sample of stolen data")}</div>
                    {post.sample.map((s, i) => (
                      <div key={i} className="leak-sample-row">{"\u{1F4C4}"} {s}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {live && (!market || sold) && (
              <button
                className={`btn leak-continue ${speaking ? "speaking" : ""}`}
                onClick={onContinue}
                disabled={speaking}
              >
                {config.continueLabel ?? tx("What happens next?")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
